import React, { useMemo } from "react";
import {
  formatQuestTime,
  getQuestDeadlineDate,
  shouldDeferQuestDeadline,
} from "../utils/questUtils";

function isQuestResolved(quest) {
  return (
    quest?.status === "completed" ||
    quest?.completed === true ||
    quest?.claimed === true
  );
}

function isQuestFailed(quest) {
  return quest?.status === "failed";
}

function getMinutesUntilDeadline(quest, questDate, wakeTime = null) {
  if (!quest?.deadlineTime || isQuestResolved(quest) || isQuestFailed(quest)) {
    return null;
  }

  if (shouldDeferQuestDeadline(quest, wakeTime)) {
    return null;
  }

  const deadline = getQuestDeadlineDate(quest, questDate);
  if (!deadline) {
    return null;
  }

  const diff = deadline.getTime() - Date.now();
  const diffMinutes = Math.floor(diff / 60000);

  if (diffMinutes < 0 || diffMinutes > 30) {
    return null;
  }

  return diffMinutes;
}

const SystemStatusBar = ({
  profile,
  todaysQuests = [],
  pendingPenalties = [],
  questDate = null,
}) => {
  const statusLine = useMemo(() => {
    if (!profile) {
      return "System calibration underway. Hold position.";
    }

    const quests = Array.isArray(todaysQuests) ? todaysQuests : [];
    const activeQuests = quests.filter(
      (quest) => !isQuestResolved(quest) && !isQuestFailed(quest)
    );

    const unresolvedPenalty = pendingPenalties[0];
    if (unresolvedPenalty) {
      return "HUNTER. A system mandate remains unresolved. Failure will be recorded.";
    }

    if (quests.length > 0 && quests.every((quest) => isQuestResolved(quest))) {
      return "Daily objectives cleared. The System acknowledges your output.";
    }

    if (activeQuests.length === 1) {
      return "One objective remains. Do not leave it incomplete.";
    }

    const urgentDeadlineQuest = activeQuests.find(
      (quest) => getMinutesUntilDeadline(quest, questDate, profile?.wakeTime) !== null
    );
    if (urgentDeadlineQuest) {
      return `WARNING. Time window closing on: ${urgentDeadlineQuest.title}`;
    }

    if (quests.length === 0) {
      return "No objectives logged. The System is watching an idle hunter.";
    }

    return `${activeQuests.length || quests.length} objectives active. Current cycle in progress.`;
  }, [pendingPenalties, profile, questDate, todaysQuests]);

  const unresolvedPenalty = pendingPenalties[0];
  const accentClass = unresolvedPenalty
    ? "system-status-bar--danger"
    : profile?.level >= 7
      ? "system-status-bar--elevated"
      : "";

  return (
    <div className={`system-status-bar ${accentClass}`.trim()}>
      <span className="system-status-bar__label">[SYSTEM READOUT]</span>
      <span className="system-status-bar__text">{statusLine}</span>
      {unresolvedPenalty?.deadlineTime && (
        <span className="system-status-bar__meta">
          Cutoff {formatQuestTime(unresolvedPenalty.deadlineTime)}
        </span>
      )}
    </div>
  );
};

export default SystemStatusBar;
